<template>
  <div
    class="wt2-dropdown-view-component"
    :class="{
      [type]: true,
      [autoPosition]: autoAlignment,
    }"
    @mouseenter="onMouseenter"
    @mouseleave="onMouseleave"
  >
    <button
      @click="onClickToggleButton"
      :disabled="disabled"
      :class="[
        '-toggle',
        {
          '-opened': isOpen,
          '-open-on-hover': openOnHover,
        },
      ]"
    >
      <slot name="label">
        <span class="-label">{{ label }}</span>
        <i class="-icon wticon wticon-chevron-down" />
      </slot>
    </button>

    <div
      v-if="!transientView || isOpen"
      v-show="isOpen"
      class="-box wt2-color-normal"
      :style="boxStyle"
    >
      <div
        v-if="showArrow"
        class="-triangle"
        :style="{ left: triangleOffset + 'px' }"
      />
      <div class="-content">
        <slot name="view" :close="close" ref="view">
          <!--// fallback content here -->
          <span>// placeholder: wt-dropdown-view content...</span>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import app from 'app';

export default {
  props: {
    // transientView: view slot will exists only when opened
    transientView: { type: Boolean, default: false },
    openOnHover: { type: Boolean, default: false },
    label: { type: String, default: '...' },
    disabled: { type: Boolean, default: false },
    disableClickOutside: { type: Boolean, default: false },
    type: { type: String, default: '' },
    showArrow: { type: Boolean, default: false },
    // boxOffset is the offset we want to give to the drop down when displayed with an arrow.
    boxOffset: { type: Number, default: 50 },
    offsetTop: { type: Number, default: 0 },
    offsetTopItemClass: { type: String, default: null },
    offsetBottomItemClass: { type: String, default: null },
    autoHeight: { type: Boolean, default: false },
  },

  computed: {
    rightAlignment() {
      return /\bright\b/.test(this.type);
    },
    topAlignment() {
      return /\btop\b/.test(this.type);
    },
    autoAlignment() {
      return /\bauto\b/.test(this.type);
    },
  },

  created() {
    this.boxStyle.zIndex = this.openOnHover ? 150 : 140;
  },

  data() {
    return {
      autoPosition: '',
      guid: app.guid(),
      isOpen: false,
      boxStyle: {
        zIndex: 0,
        transform: 'translateX(0)',
      },
      triangleOffset: 0,
      kaHandlers: [
        {
          on: 'keydown',
          key: $.ui.keyCode.ESCAPE,
          run: () => {
            this.isOpen = false;
          },
        },
      ],
    };
  },

  beforeDestroy() {
    document.removeEventListener('click', this.$_clickOutside);
    app.keyboardAccessibility.remove(this.guid);
  },

  mounted() {
    document.addEventListener('click', this.$_clickOutside);
  },

  watch: {
    isOpen() {
      if (this.isOpen) {
        app.keyboardAccessibility.add(this.guid, this.kaHandlers);
        this.$_setBoxSlidingNextTick();
        this.$nextTick(() => {
          this.$emit('opened');
        });
      } else {
        app.keyboardAccessibility.remove(this.guid);
        this.$emit('closed');
      }
    },
  },

  methods: {
    onClickToggleButton(e) {
      if (!this.openOnHover) {
        this.onToggle();
      }

      this.$emit('on-toggle-click', e);
    },

    onMouseenter: function (e) {
      // fix for: DEV-7295
      if (e && e.relatedTarget === null) {
        return;
      }
      this.openOnHover && this.onToggle(true);
    },

    onMouseleave: function (e) {
      // fix for: DEV-7295
      if (e && e.relatedTarget === null) {
        return;
      }
      this.openOnHover && this.onToggle(false);
    },

    onToggle(isOpen) {
      this.isOpen = _.isDefined(isOpen) ? isOpen : !this.isOpen;
      this.$emit('toggle', this.isOpen);
    },

    close() {
      this.isOpen = false;
    },
    isTopPosition() {
      const $button = this.$el.querySelector('.-toggle');
      const offsetRect = this.$el.offsetParent.getBoundingClientRect();
      const buttonRect = $button.getBoundingClientRect();
      return (
        buttonRect.top - offsetRect.top > offsetRect.bottom - buttonRect.bottom
      );
    },
    $_setBoxSlidingNextTick() {
      this.boxStyle = Object.assign(this.boxStyle, {
        transform: 'none',
        height: 'auto',
      });
      this.$nextTick(() => {
        this.$_setBoxSliding();
      });
    },

    $_setBoxSliding() {
      if (!this.$el || !this.isOpen) {
        return;
      }

      const $box = this.$el.querySelector('.-box');
      const $button = this.$el.querySelector('.-toggle');
      const rect = $box.getBoundingClientRect();
      const buttonRect = $button.getBoundingClientRect();
      let boxShift = 0;

      const boxOffset = this.showArrow ? this.boxOffset : 0;

      const viewportWidth = Math.max(
        document.documentElement.clientWidth,
        window.innerWidth || 0
      );
      if (this.rightAlignment) {
        // alignmentOffset is tje offet required to align the right side of the drop down with the right side of the
        // toggle element
        const alignmentOffset = rect.width - buttonRect.width;
        if (
          rect.left - alignmentOffset + rect.width + boxOffset >
          viewportWidth
        ) {
          // the right side of the drop down is out of screen
          boxShift = rect.left + rect.width - viewportWidth;
        } else if (rect.left - alignmentOffset + boxOffset < 0) {
          // the left side of the drop down is out of screen
          boxShift = rect.left;
        } else {
          boxShift = alignmentOffset - boxOffset;
        }
      } else {
        if (rect.left - boxOffset + rect.width > viewportWidth) {
          // the right side of the drop down is out of screen
          boxShift = rect.left + rect.width - viewportWidth;
        } else if (rect.left - boxOffset < 0) {
          // the left side of the drop down is out of screen
          boxShift = rect.left;
        } else {
          boxShift = boxOffset;
        }
      }
      let xOffset = 0;
      let yOffset = 0;
      let height = rect.height;
      const isTop =
        this.topAlignment || (this.autoAlignment && this.isTopPosition());

      if (isTop) {
        this.autoPosition = 'top';
        if (this.offsetTop > 0 && this.offsetTopItemClass) {
          height =
            buttonRect.top -
            document
              .querySelector(this.offsetTopItemClass)
              .getBoundingClientRect().bottom -
            this.offsetTop;
        }

        yOffset -= height + buttonRect.height;
      } else {
        this.autoPosition = 'bottom';
        if (this.offsetBottomItemClass) {
          height =
            document
              .querySelector(this.offsetBottomItemClass)
              .getBoundingClientRect().top -
            this.offsetTop -
            buttonRect.bottom;
        }
      }

      this.triangleOffset = boxShift + buttonRect.width / 2;
      if (boxShift !== 0) {
        xOffset -= boxShift;
        if (this.showArrow) {
          isTop ? (yOffset -= 12) : (yOffset += 12);
        }
      }
      if (xOffset || yOffset) {
        this.boxStyle = Object.assign(this.boxStyle, {
          transform: `translateX(${xOffset}px) translateY(${yOffset}px)`,
          height: this.autoHeight ? 'auto' : `${height}px`,
        });
      }
    },

    $_clickOutside(e) {
      if (this.disableClickOutside || this.openOnHover) {
        return;
      }

      const isAttached = document.contains(e.target);
      const isInside = this.$el.contains(e.target);
      if (isAttached && !isInside) {
        this.close();
      }
    },
  },
};
</script>
